import React, { useContext, useState } from 'react'
import WizardModal from '../../../../../components/WizardModal'
import IssueTypeStep from './steps/IssueTypeStep'
import ReportTypeStep from './steps/ReportTypeStep'
import GeneralInquiryStep, { GeneralInquiryData } from './steps/GeneralInquiryStep'
import LocationStep from './steps/LocationStep'
import UrgencyStep from './steps/UrgencyStep'
import AffectsNeighboursStep from './steps/AffectsNeighboursStep'
import DescriptionStep from './steps/DescriptionStep'
import AttachmentsStep, { AttachmentEntry } from './steps/AttachmentsStep'
import ReviewStep from './steps/ReviewStep'
import GasEmergencyScreen from './steps/GasEmergencyScreen'
import SubmittedScreen from './SubmittedScreen'
import { repairAndInquiriesApi, CreateServiceRequestDto } from '../../../../../api/repairAndInquiries'
import { generateError } from '../../../../../services/error.service'
import { GlobalContext } from '../../../../../App'
import { ContextEntity } from '../../../../../customEntites/context.entity'

interface NewDamageReportWizardProps {
  isOpen: boolean
  onClose: () => void
  onSubmitted?: () => void
}

export interface WizardState {
  issueType: string
  reportType: string
  location: string
  urgency: string
  affectsNeighbours: string
  problemDescription: string
  attachments: AttachmentEntry[]
  inquiry: GeneralInquiryData
}

const INITIAL_STATE: WizardState = {
  issueType: '',
  reportType: '',
  location: '',
  urgency: '',
  affectsNeighbours: '',
  problemDescription: '',
  attachments: [] as AttachmentEntry[],
  inquiry: { subject: '', description: '', attachments: [], isUrgent: false },
}

const CATEGORY_MAP: Record<string, string> = {
  plumbing: '1', electrical: '2', heating: '3', appliance: '4', other: '5',
}
const URGENCY_MAP: Record<string, string> = {
  emergency: '1', high: '2', medium: '3', low: '4',
}
const ISSUE_LABELS: Record<string, string> = {
  plumbing: 'Plumbing', electrical: 'Electrical',
  heating: 'Heating', appliance: 'Appliance', other: 'Other',
}

const NewDamageReportWizard = ({ isOpen, onClose, onSubmitted }: NewDamageReportWizardProps) => {
  const { context }: ContextEntity = useContext(GlobalContext)
  const [step, setStep] = useState(0)
  const [state, setState] = useState<WizardState>(INITIAL_STATE)
  const [submittedTicketId, setSubmittedTicketId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [showGasEmergency, setShowGasEmergency] = useState(false)

  const handleClose = () => {
    onClose()
    setTimeout(() => {
      setStep(0)
      setState(INITIAL_STATE)
      setSubmittedTicketId(null)
      setSubmitError('')
      setShowGasEmergency(false)
    }, 200)
  }

  const isDamage = state.reportType === 'damage'
  const totalSteps = isDamage ? 8 : 3

  const handleSubmit = async () => {
    if (isSubmitting || state.issueType === 'gas_leak') return
    const contract = context.contracts.activeContracts[0]
    setIsSubmitting(true)
    setSubmitError('')
    try {
      const dto: CreateServiceRequestDto = isDamage ? {
        subject: `Damage Report - ${ISSUE_LABELS[state.issueType] ?? state.issueType}`,
        category: CATEGORY_MAP[state.issueType] ?? '5',
        description: JSON.stringify({
          requestType: 'damage_report',
          location: state.location,
          urgency: state.urgency,
          affectsNeighbours: state.affectsNeighbours,
          description: state.problemDescription,
        }),
        urgency: URGENCY_MAP[state.urgency] ?? '4',
        contractNumber: contract.contractNumber,
        contract_id: contract.contractId,
        contact_id: contract.contactId,
        name: contract.name,
        phone: contract.phone,
        email: contract.email,
        imageReferences: JSON.stringify(state.attachments.map((a) => a.imageReference)),
      } : {
        subject: state.inquiry.subject,
        category: '5',
        description: JSON.stringify({
          requestType: 'general_request',
          description: state.inquiry.description,
        }),
        urgency: state.inquiry.isUrgent ? '1' : '4',
        contractNumber: contract.contractNumber,
        contract_id: contract.contractId,
        contact_id: contract.contactId,
        name: contract.name,
        phone: contract.phone,
        email: contract.email,
      }
      const attachments = isDamage
        ? state.attachments.map((a) => a.file)
        : state.inquiry.attachments
      const res = await repairAndInquiriesApi.createServiceRequest(dto, attachments)
      setSubmittedTicketId(res?.ticket_number ?? res?.sr_number ?? '')
      onSubmitted?.()
    } catch (err: any) {
      setSubmitError(generateError(err?.response?.data?.message))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1)
    } else {
      handleSubmit()
    }
  }

  const handleBack = step > 0 ? () => setStep(step - 1) : undefined

  const autoAdvanceSteps = isDamage ? [0, 1, 2, 3, 4] : [0, 1]
  const isLastStep = step === totalSteps - 1
  const hideNext = autoAdvanceSteps.includes(step)

  const isNextDisabled = (isLastStep && !isDamage && (!state.inquiry.subject.trim() || !state.inquiry.description.trim())) || isSubmitting

  const stepLabels = isDamage
    ? ['Issue Type', 'Report Type', 'Location', 'Urgency', 'Neighbours', 'Description', 'Attachments', 'Review']
    : ['Issue Type', 'Report Type', 'General Inquiry']

  if (!isOpen) return null

  if (submittedTicketId) {
    const submittedWithAttachments =
      (isDamage && state.attachments.length > 0) ||
      (!isDamage && state.inquiry.attachments.length > 0)
    return <SubmittedScreen ticketId={submittedTicketId} onBack={handleClose} hasAttachments={submittedWithAttachments} />
  }

  if (showGasEmergency) {
    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100vh',
          zIndex: 100,
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflowY: 'auto',
        }}
      >
        <div style={{ width: "100%", maxWidth: 560, padding: "32px" }}>
          <GasEmergencyScreen
            onBack={() => {
              setShowGasEmergency(false)
              setState((prev) => ({ ...prev, issueType: '' }))
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      {isSubmitting && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              border: '5px solid #e8f4fc',
              borderTopColor: '#218cda',
              borderRadius: '50%',
              animation: 'ndr-spin 0.8s linear infinite',
            }}
          />
          <style>{`@keyframes ndr-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    <WizardModal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Damage Report"
      stepLabel={`Step ${step + 1} of ${totalSteps} — ${stepLabels[step]}`}
      totalSteps={totalSteps}
      currentStep={step}
      onBack={handleBack}
      onNext={handleNext}
      nextLabel={isLastStep ? (isSubmitting ? 'Submitting…' : 'Submit Request') : 'Next'}
      nextDisabled={isNextDisabled}
      hideNext={hideNext}
      errorMessage={isLastStep ? submitError : undefined}
    >
      {step === 0 && (
        <IssueTypeStep
          value={state.issueType}
          onEmergencySelect={() => {
            setState((prev) => ({ ...prev, issueType: 'gas_leak' }))
            setShowGasEmergency(true)
          }}
          onChange={(issueType) => { setState(prev => ({ ...prev, issueType })); handleNext() }}
        />
      )}
      {step === 1 && (
        <ReportTypeStep
          value={state.reportType}
          onChange={(reportType) => { setState(prev => ({ ...prev, reportType })); handleNext() }}
        />
      )}
      {step === 2 && isDamage && (
        <LocationStep
          value={state.location}
          onChange={(location) => { setState(prev => ({ ...prev, location })); handleNext() }}
        />
      )}
      {step === 3 && isDamage && (
        <UrgencyStep
          value={state.urgency}
          onChange={(urgency) => { setState(prev => ({ ...prev, urgency })); handleNext() }}
        />
      )}
      {step === 4 && isDamage && (
        <AffectsNeighboursStep
          value={state.affectsNeighbours}
          onChange={(affectsNeighbours) => { setState(prev => ({ ...prev, affectsNeighbours })); handleNext() }}
        />
      )}
      {step === 5 && isDamage && (
        <DescriptionStep
          value={state.problemDescription}
          onChange={(problemDescription) => setState(prev => ({ ...prev, problemDescription }))}
        />
      )}
      {step === 6 && isDamage && (
        <AttachmentsStep
          files={state.attachments}
          category={CATEGORY_MAP[state.issueType] ?? '5'}
          onChange={(attachments) => setState(prev => ({ ...prev, attachments }))}
        />
      )}
      {isDamage && step === 7 && (
        <ReviewStep
          issueType={state.issueType}
          reportType={state.reportType}
          location={state.location}
          urgency={state.urgency}
          affectsNeighbours={state.affectsNeighbours}
          description={state.problemDescription}
          attachments={state.attachments}
        />
      )}
      {!isDamage && step === 2 && (
        <GeneralInquiryStep
          value={state.inquiry}
          onChange={(inquiry) => setState(prev => ({ ...prev, inquiry }))}
        />
      )}
    </WizardModal>
    </>
  )
}

export default NewDamageReportWizard
